import React from 'react';
import { useRouter } from 'next/router';

import withLayoutHOF, { HofStaticPropsType } from 'components/withLayoutHOF';
import { client } from 'lib/apollo/client';
import { RELATED_ARTICLES, DEFAULT_REVALIDATION_TIME } from 'utils/shared';
import SlugPage from 'components/SlugPage';
import { GetStaticPathsResult, InferGetStaticPropsType } from 'next';
import { extractPostOption } from 'utils/helpers';
import { parseImageUrl } from 'utils/imageUtils';
import {
  GetPostPageInfoDocument,
  GetPrevNextArticleWithRelatedArticlesFallbackDocument,
  SlugPageDocument,
  SlugPagePathsDocument,
} from 'gql/graphql-operations';
import validateProps from 'utils/props-validations/blogpost';
import getPreviewPostId from 'utils/getPreviewPostId';
import { addDefaultValues } from 'utils/seoHead';

type SinglePostProps = Omit<InferGetStaticPropsType<typeof getStaticProps>, keyof HofStaticPropsType>;

export type { SinglePostProps };

export default function SinglePostPage({
  post,
  relatedArticles,
  relatedServices,
  hideAuthor,
  hideComments,
  hideSharing,
  showKeyQuestions,
  keyQuestions,
  partOfSeries,
  postSeries,
  preview,
  errors,
  keyQuestionLdJson,
  previousArticle,
  nextArticle,
  url,
  hasArticlesContentsLinks,
}: SinglePostProps): JSX.Element {
  const router = useRouter();

  // check if the page is not generated
  // if so, show a loading message while we generate it
  if (router.isFallback) {
    return <div>Please wait, loading page...</div>;
  }

  return (
    <SlugPage
      errors={errors}
      preview={preview}
      post={post}
      relatedArticles={relatedArticles}
      relatedServices={relatedServices}
      url={url}
      hideAuthor={hideAuthor}
      hideComments={hideComments}
      hideSharing={hideSharing}
      showKeyQuestions={showKeyQuestions}
      keyQuestions={keyQuestions}
      partOfSeries={partOfSeries}
      postSeries={postSeries}
      keyQuestionLdJson={keyQuestionLdJson}
      previousArticle={previousArticle}
      nextArticle={nextArticle}
      hasArticlesContentsLinks={hasArticlesContentsLinks}
    />
  );
}

export const BLOG_POST_REVALIDATION_TIME = DEFAULT_REVALIDATION_TIME;

const maxRetry = process.env.NEXT_PUBLIC_MAX_RETRY ?? 3;
let retryTentatives = 0;

const getPostData = async (params: any, preview?: boolean) => {
  if (!client) {
    throw new Error(`Internal Server Error. Error code: CLIENT_MISSING`);
  }

  const { slug, postId } = params;

  const { data, errors } = await client.query({
    query: SlugPageDocument,
    variables: {
      isPreview: preview,
      ...{
        postId,
        uri: !postId ? slug ?? '/' : undefined,
      },
    },
  });

  if (errors) {
    throw new Error(`GET_POST_BY_SLUG: ${JSON.stringify(errors)}`);
  }

  const { post } = data ?? {};
  if (preview) return post?.preview?.node;
  return post;
};

const getPrevNextArticles = async (
  postId: number,
  includeFallback: boolean = false,
  categoryId?: number,
  perPage?: number,
  offset?: number | null,
  excludeIds?: string[],
) => {
  if (!client) {
    throw new Error(`Internal Server Error. Error code: CLIENT_MISSING`);
  }

  const { data, errors } = await client.query({
    query: GetPostPageInfoDocument,
    variables: {
      id: postId,
    },
  });

  if (errors) {
    throw new Error(`GET_PREV_NEXT_ARTICLES: ${JSON.stringify(errors)}`);
  }

  const { posts } = data ?? {};

  const prevNextId = posts?.pageInfo?.startCursor!;

  const { data: prevNextData, errors: prevNextErrors } = await client.query({
    query: GetPrevNextArticleWithRelatedArticlesFallbackDocument,
    variables: {
      startCursor: prevNextId,
      includeFallback,
      categoryId,
      perPage,
      offset,
      exclude: excludeIds,
    },
  });

  if (prevNextErrors) {
    throw new Error(`GET_PREV_NEXT_ARTICLES: ${JSON.stringify(prevNextErrors)}`);
  }

  return {
    previousArticle: prevNextData.previousPost?.nodes[0] ?? null,
    nextArticle: prevNextData.nextPost?.nodes[0] ?? null,
    autoRelatedPosts:
      prevNextData.posts?.edges?.map((p) => {
        if (!p?.node) return null;
        return { ...p.node, date: p.node?.date ? new Date(p.node.date ?? null).toUTCString() : null };
      }) ?? [],
  };
};

export const getStaticProps = withLayoutHOF(async ({ params, preview = false, previewData }) => {
  if (!client) {
    throw new Error(`Internal Server Error. Error code: CLIENT_MISSING`);
  }

  const postId = getPreviewPostId(typeof params?.slug === 'string' ? params.slug : '', previewData);

  let post = await getPostData({ ...params, postId }, preview);
  if (!post) {
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < +maxRetry; i++) {
      // eslint-disable-next-line operator-assignment
      retryTentatives = retryTentatives + 1;
      // eslint-disable-next-line no-await-in-loop, @typescript-eslint/no-loop-func
      post = await getPostData(params);
      if (post) break;
    }
  }

  if (!post || (post.status !== 'publish' && !preview)) {
    retryTentatives = 0;
    const notFound = {
      notFound: true,
    } as const;
    return notFound;
  }

  /**
   * Single post options
   */
  const {
    hideAuthor,
    hideComments,
    hideSharing,
    showKeyQuestions,
    keyQuestions,
    automaticRelatedPosts,
    relatedPosts,
    hideArticleContents,
  } = extractPostOption(post.postOptions ?? {});

  // This is used to exclude already fetched posts and make all posts
  // in the page unique.
  const excludeIds = [post.id];

  /**
   * Previous and Next articles
   */
  const { previousArticle, nextArticle, autoRelatedPosts } = await getPrevNextArticles(
    post.databaseId,
    automaticRelatedPosts,
    post.categories?.nodes?.[0]?.databaseId,
    RELATED_ARTICLES,
    null,
    excludeIds,
  );

  /**
   * Related Articles
   */
  const selectedRelatedPosts =
    relatedPosts?.map((p) => {
      if (!p) return null;
      return { ...p, date: p?.date ? new Date(p.date).toUTCString() : null };
    }) ?? [];

  const relatedArticles = automaticRelatedPosts ? autoRelatedPosts : selectedRelatedPosts;

  /**
   * Key question LdJson FAQ generator
   */

  let keyQuestionLdJson = {
    raw: '',
  };

  if (keyQuestions) {
    const jsonLd = {
      // eslint-disable-next-line
      raw: `<script type=\"application/ld+json\">{\"@context\": \"https://schema.org\",\"@type\": \"FAQPage\",\"mainEntity\": [${keyQuestions?.map(
        (keyQuestion) => {
          const { question, answer } = keyQuestion ?? {};
          // eslint-disable-next-line
          return `{\"@type\": \"Question\",\"name\": \"${question}\",\"acceptedAnswer\": {\"@type\": \"Answer\",\"text\": \"${answer}\"}}`;
        },
      )}]}</script>`,
    };

    keyQuestionLdJson = jsonLd;
  }

  /**
   * Post Series
   */
  const partOfSeries = (post?.series?.nodes?.length ?? 0) > 0;
  const postSeries = post.series?.nodes?.[0] ?? null;

  /**
   * Seo data
   */
  // @TODO: remove this checking after updating rank math plugin
  const seo = preview
    ? null
    : {
        ...post.seo,
        openGraph: {
          ...post.seo?.openGraph,
          articleMeta: {
            publishedTime: post?.date,
            ...(post.seo?.openGraph?.articleMeta ?? null),
          },
          image: {
            height: post.featuredImage?.node?.mediaDetails?.height ?? 0,
            width: post.featuredImage?.node?.mediaDetails?.width ?? 0,
            secureUrl: parseImageUrl(post.featuredImage?.node?.sourceUrl ?? ''),
            url: parseImageUrl(post.featuredImage?.node?.sourceUrl ?? ''),
            type: post.featuredImage?.node?.mimeType ?? '',
          },
        },
        ogImageAlt: post.featuredImage?.node?.altText ?? '',
      };
  const url = `${process.env.NEXT_PUBLIC_HOSTNAME}${process.env.NEXT_PUBLIC_BASE_PATH}/${params?.slug}`;

  const hasArticlesContentsLinks =
    !hideArticleContents && (post.content?.match(/<h2[^>]*>(.*?)<\/h2>/g)?.length ?? 0) >= 2;

  const contentWithReplacedImages = parseImageUrl(post.content ?? '');

  const props = {
    post: { ...post, content: contentWithReplacedImages },
    relatedArticles:
      relatedArticles?.map((article) => ({ label: article?.title ?? '', href: `/${article?.slug}` })) ?? [],
    relatedServices:
      post.blogPostRelatedServices?.realtedServices?.map((service) => ({
        label: service?.serviceLabel ?? '',
        href: service?.serviceUrl ?? '',
      })) ?? [],
    hideAuthor,
    hideComments,
    hideSharing,
    showKeyQuestions,
    keyQuestions,
    partOfSeries,
    postSeries,
    seo: addDefaultValues(seo ?? {}),
    preview,
    errors: null as string | null,
    keyQuestionLdJson,
    previousArticle,
    nextArticle,
    url,
    bgFooter: 'Dark',
    hasArticlesContentsLinks,
  };

  const errors = validateProps(props);
  if (errors && !preview) throw new Error(errors);
  if (preview) props.errors = errors;
  return {
    props,
    // revalidate: BLOG_POST_REVALIDATION_TIME,
  };
}, 'Single post');

export async function getStaticPaths() {
  if (!client) {
    throw new Error(`Internal Server Error. Error code: CLIENT_MISSING`);
  }

  const { data, errors } = await client.query({
    query: SlugPagePathsDocument,
  });

  if (errors) {
    throw new Error(`GET_POST_SLUGS: ${JSON.stringify(errors)}`);
  }

  const paths =
    data?.posts?.nodes?.reduce((acc: GetStaticPathsResult['paths'], post) => {
      if (post?.slug) acc.push({ params: { slug: post.slug } });
      return acc;
    }, []) ?? [];

  return {
    paths,
    fallback: 'blocking',
  };
}
